import React from "react";
import { Link } from "react-router-dom";
import { sessionsApi } from "@/api/client";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "@/components/layout/PageHeader";
import { MapPin, User, Tag } from "lucide-react";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";

export default function GradeSessionsPage({
  title,
  barColor,
  gradeBand,
  location,
  agenda,
  keynoteSpeaker,
  sessionsList,
  accessCode = "PKMQ-QXTR-FTVKJ",
  registrationNumber = "344824",
  testId,
}) {
  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ["sessions", gradeBand],
    queryFn: () => sessionsApi.list({ grade_band: gradeBand }),
  });

  return (
    <div className="space-y-6 pb-12" data-testid={testId}>
      <PageHeader title={title} barColor={barColor} />

      <div className="bg-card rounded-xl border border-border p-4">
        <h2 className="font-semibold text-base text-foreground mb-3">Location</h2>
        <div className="text-sm text-muted-foreground space-y-1">
          {location ? (
            <>
              <p><strong>{location.name}</strong></p>
              <p>{location.address}</p>
            </>
          ) : (
            <p>Location to be announced.</p>
          )}
        </div>
      </div>

      <div className="bg-card rounded-xl border border-border p-4">
        <h2 className="font-semibold text-base text-foreground mb-3">
          Registration Information
        </h2>
        <div className="text-sm text-muted-foreground space-y-2">
          <p>
            <strong>All participants are required to register by May 31, 2026.</strong>
          </p>
          <p>
            Registration must be completed through PowerSchool PD using
            registration number <strong>{registrationNumber}</strong>. Participants will
            receive 12 CEUs if all activities are completed.
          </p>
          <p>
            Timely registration is important to ensure accurate group
            assignments, session planning, and materials preparation. Lunch
            will be provided for all registered participants.
          </p>
          <p>
            <strong>On-site registration will not be available.</strong>
          </p>
          <p>
            If you need assistance with registration, please contact the
            district's professional development office or click on the link
            below for more information.
          </p>
          <div className="pt-1 space-y-1">
            <p>
              <a
                href="https://alsde.truenorthlogic.com/ia/empari/login/index"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline hover:opacity-80 font-medium"
                data-testid="powerschool-pd-link"
              >
                PowerSchool PD
              </a>
            </p>
            <p>
              <a
                href="https://drive.google.com/drive/folders/1t9UJC9-blQl7rZIBDFjSxUcDg3AEjIoT"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline hover:opacity-80 font-medium"
                data-testid="powerschool-assistance-link"
              >
                Assistance with PowerSchool PD Registration
              </a>
            </p>
          </div>
        </div>
      </div>

      <div className="bg-card rounded-xl border border-border p-4">
        <h2 className="font-semibold text-base text-foreground mb-3">
          Agenda at a Glance
        </h2>
        {agenda && agenda.length > 0 ? (
          <div className="space-y-2 text-sm text-muted-foreground">
            {agenda.map((a) => (
              <div key={a.time} className="flex gap-3">
                <span className="font-medium min-w-32">{a.time}</span>
                <span className={a.bold ? "font-bold" : ""}>{a.label}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Agenda to be announced.
          </p>
        )}
      </div>

      {isLoading && (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
        </div>
      )}

      {!isLoading && sessions.length > 0 && (
        <div className="space-y-6" data-testid="sessions-list">
          {sessions.map((session, i) => (
            <motion.div
              key={session.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-card rounded-xl border border-border p-4"
              data-testid="session-card"
            >
              <h3 className="font-semibold text-base text-foreground mb-2">
                {session.title}
              </h3>
              {session.description && (
                <div className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  <ReactMarkdown
                    components={{
                      a: ({ children, href }) => (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary underline hover:opacity-80"
                        >
                          {children}
                        </a>
                      ),
                    }}
                  >
                    {session.description}
                  </ReactMarkdown>
                </div>
              )}
              <div className="flex flex-wrap gap-3 mt-3">
                {session.presenter && (
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <User className="w-3 h-3" />
                    <span>{session.presenter}</span>
                  </div>
                )}
                {session.location && (
                  <div className="flex gap-1 text-sm text-muted-foreground">
                    <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />
                    <span>{session.location}</span>
                  </div>
                )}
                {session.time_slot && (
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Tag className="w-3 h-3" />
                    <span>{session.time_slot}</span>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <div className="bg-card rounded-xl border border-border p-4">
        <h2 className="font-semibold text-base text-foreground mb-3">
          Keynote Speaker
        </h2>
        {keynoteSpeaker ? (
          <div className="flex flex-col sm:flex-row gap-4">
            <img
              src={keynoteSpeaker.photo}
              alt={keynoteSpeaker.name}
              className="w-32 h-32 sm:w-40 sm:h-40 rounded-xl object-cover flex-shrink-0"
              data-testid="keynote-speaker-photo"
            />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-lg text-foreground">
                {keynoteSpeaker.name}
              </p>
              {keynoteSpeaker.title && (
                <p className="text-sm text-muted-foreground mt-0.5 whitespace-pre-line">
                  {keynoteSpeaker.title}
                </p>
              )}
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed whitespace-pre-line">
                {keynoteSpeaker.bio}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground font-bold">
            Information coming soon.
          </p>
        )}
      </div>

      <div className="bg-card rounded-xl border border-border p-4">
        <h2 className="font-semibold text-base text-foreground mb-3">
          Sessions
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          Bring your device.
        </p>
        {sessionsList && sessionsList.length > 0 ? (
          <div className="space-y-5" data-testid="static-sessions-list">
            {[...sessionsList]
              .sort((a, b) =>
                a.title.localeCompare(b.title, undefined, { sensitivity: "base" })
              )
              .map((s, i) => (
                <div
                  key={i}
                  className="border-b border-border last:border-0 pb-4 last:pb-0"
                  data-testid="static-session-item"
                >
                  <p className="font-semibold text-base text-foreground mb-2">
                    {s.title}
                  </p>
                  <div
                    className={`text-sm text-muted-foreground leading-relaxed whitespace-pre-line ${
                      s.bold ? "font-bold" : ""
                    }`}
                  >
                    {s.description}
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground font-bold">
            Information coming soon.
          </p>
        )}
      </div>

      <div className="bg-card rounded-xl border border-border p-4">
        <h2 className="font-semibold text-base text-foreground mb-3">
          Pre and Post Assignments
        </h2>
        <div className="text-sm text-muted-foreground space-y-2 leading-relaxed">
          <p>
            The pre and post assignments for this professional development are
            located in Schoology. To access the assignments, click on the link
            below and log in to your Schoology account. If you experience any
            issues accessing the platform, please review the support video to
            ensure you are logging in correctly.
          </p>
          <p>To enroll in the course, use the access code below to join:</p>
          <p className="font-semibold text-foreground">
            Access Code:{" "}
            <span className="font-mono tracking-wide bg-muted px-2 py-0.5 rounded">
              {accessCode}
            </span>
          </p>
          <p>
            Enter the access code in Schoology to register and gain access to
            the assignments. Please complete both the pre and post assignments
            to earn the 12 CEUs.
          </p>
          <p className="font-bold text-foreground">
            All Pre Assignments are due 11:59 pm June 7, 2026 and all Post
            Assignments are due 11:59 pm June 30, 2026.
          </p>
          <p className="pt-1">
            <a
              href="https://drive.google.com/file/d/1FGjnYD45z-eS-KBIjuL9jxqMC0XuEmfw/view"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline hover:opacity-80 font-medium"
              data-testid="schoology-support-video-link"
            >
              Schoology Support Video
            </a>
          </p>
          <p>
            <a
              href="https://drive.google.com/file/d/1XKow3oHau_25uI-xZVBB8nvPgAe0kiCt/view"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline hover:opacity-80 font-medium"
              data-testid="schoology-access-code-link"
            >
              Joining a Schoology Course with an Access Code
            </a>
          </p>
        </div>
      </div>

      <div className="pt-2">
        <Link
          to="/resources"
          className="inline-flex items-center gap-2 text-base font-semibold text-primary underline hover:opacity-80"
          data-testid="resources-page-link"
        >
          Resources
        </Link>
      </div>
    </div>
  );
}
